import {Capacitor} from '@capacitor/core';
import {
  AnimationOptions,
  BackgroundColorOptions,
  StatusBar,
  Style as StatusBarStyle,
  StyleOptions,
} from '@capacitor/status-bar';
import {isRenderedAsAndroid} from '@common/core/platform-detection';

const rgbToHex = (rgb: string): string => {
  const [r, g, b] = rgb.split(' ').map(Number);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b)
    .toString(16)
    .slice(1)
    .toUpperCase()}`;
};

const isColorDark = (hexColor: string): boolean => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
  return brightness < 128;
};

const Overlay = async (state: boolean) => {
  if (!Capacitor.isNativePlatform()) return;
  await StatusBar.setOverlaysWebView({overlay: state});
};

const BackgroundColor = async (props: BackgroundColorOptions) => {
  if (!Capacitor.isNativePlatform()) return;
  await StatusBar.setBackgroundColor(props);
};

const Style = async (props: StyleOptions) => {
  if (!Capacitor.isNativePlatform()) return;
  await StatusBar.setStyle(props);
};

interface ThemeOptions {
  isDark?: boolean;
}

const Theme = async (props?: ThemeOptions) => {
  if (!Capacitor.isNativePlatform()) return;

  const computedStyles = getComputedStyle(document.documentElement);
  const beBackground = rgbToHex(
    computedStyles.getPropertyValue('--be-background').trim(),
  );

  const resolvedProps = props ?? {
    isDark: isColorDark(beBackground),
  };

  await BackgroundColor({color: beBackground});
  await Style({
    style: resolvedProps.isDark ? StatusBarStyle.Dark : StatusBarStyle.Light,
  });
};

interface SafeAreaOptions {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  auto?: boolean;
}

const SafeArea = async (props: SafeAreaOptions) => {
  if (!Capacitor.isNativePlatform()) return;

  if (props.auto) {
    if (isRenderedAsAndroid()) {
      document.documentElement.style.setProperty(
        '--safe-area-inset-top',
        'env(safe-area-inset-top, 24px)',
      );
      document.documentElement.style.setProperty(
        '--safe-area-inset-right',
        'env(safe-area-inset-right, 0px)',
      );
      document.documentElement.style.setProperty(
        '--safe-area-inset-bottom',
        'env(safe-area-inset-bottom, 24px)',
      );
      document.documentElement.style.setProperty(
        '--safe-area-inset-left',
        'env(safe-area-inset-left, 0px)',
      );
    } else {
      document.documentElement.style.setProperty(
        '--safe-area-inset-top',
        'env(safe-area-inset-top, 0px)',
      );
      document.documentElement.style.setProperty(
        '--safe-area-inset-right',
        'env(safe-area-inset-right, 0px)',
      );
      document.documentElement.style.setProperty(
        '--safe-area-inset-bottom',
        'env(safe-area-inset-bottom, 0px)',
      );
      document.documentElement.style.setProperty(
        '--safe-area-inset-left',
        'env(safe-area-inset-left, 0px)',
      );
    }
  } else {
    if (props.top) {
      document.documentElement.style.setProperty(
        '--safe-area-inset-top',
        `env(safe-area-inset-top, ${props.top}px)`,
      );
    }
    if (props.right) {
      document.documentElement.style.setProperty(
        '--safe-area-inset-right',
        `env(safe-area-inset-right, ${props.right}px)`,
      );
    }
    if (props.bottom) {
      document.documentElement.style.setProperty(
        '--safe-area-inset-bottom',
        `env(safe-area-inset-bottom, ${props.bottom}px)`,
      );
    }
    if (props.left) {
      document.documentElement.style.setProperty(
        '--safe-area-inset-left',
        `env(safe-area-inset-left, ${props.left}px)`,
      );
    }
  }
};

interface InitializeOptions {
  overlay?: boolean;
  safeArea?: SafeAreaOptions;
  theme?: ThemeOptions;
}

const Initialize = async ({
  overlay = true,
  safeArea = {auto: true},
  theme,
}: InitializeOptions = {}) => {
  if (!Capacitor.isNativePlatform()) return;

  await SafeArea(safeArea);

  if (theme) {
    await Theme(theme);
  }

  await Overlay(overlay);
};

const show = async (options?: AnimationOptions) => {
  if (!Capacitor.isNativePlatform()) return;
  await StatusBar.show(options);
};

const hide = async (options?: AnimationOptions) => {
  if (!Capacitor.isNativePlatform()) return;
  await StatusBar.hide(options);
};

const getInfo = async () => {
  if (!Capacitor.isNativePlatform()) return;
  return await StatusBar.getInfo();
};

export const AppStatusBar = {
  Initialize,
  show,
  hide,
  getInfo,
  Overlay,
  BackgroundColor,
  Style,
  Theme,
  SafeArea,
};

(window as any).AppStatusBar = AppStatusBar;
